/**
 * Einwilligung in den Gesichtsabgleich (DSGVO Art. 9 Abs. 2 lit. a).
 *
 * Wird NUR bei den biometrischen Modi (`identity`, `full`) angezeigt, und zwar
 * VOR dem ersten Upload — eine Einwilligung, die erst nach der Verarbeitung
 * eingeholt wird, ist keine.
 *
 * Zwei Dinge sind hier bewusst so gebaut und sollten nicht "vereinfacht" werden:
 *
 * 1. Keine vorangekreuzte Box. Die Zustimmung entsteht ausschließlich durch den
 *    Klick auf die Zustimmen-Schaltfläche (Art. 4 Nr. 11: unmissverständlich
 *    bekundeter Wille). Ein vorbelegtes Kästchen wäre unwirksam.
 *
 * 2. Die Alternative führt direkt in den Ablauf ohne Biometrie (Entscheidung
 *    Claus, 01.08.2026). Sie muss irgendwohin führen, sonst ist die Wahl nicht
 *    echt und die Einwilligung nicht freiwillig (Art. 7 Abs. 4).
 *
 *    Bewusste Konsequenz: Der Gesichtsabgleich wird gerade dort angefordert, wo
 *    das Dokument selbst schwach abgesichert ist — er ist damit für den Kunden
 *    abwählbar. Wer die Prüfung trotzdem braucht, muss der Ablehnung
 *    organisatorisch nachgehen; technisch hält sie hier niemanden auf.
 *
 * Die Textfassung wird mit protokolliert (CONSENT_TEXT_VERSION). Ändert sich der
 * Text inhaltlich, MUSS diese Konstante hochgezählt werden — sonst lässt sich
 * später nicht mehr belegen, worin genau jemand eingewilligt hat.
 */
import React from 'react';
import { useI18n } from '../../i18n';

/** Bei inhaltlicher Textänderung hochzählen — sie ist Teil des Nachweises. */
export const CONSENT_TEXT_VERSION = 'de-1.0';

interface BiometricConsentProps {
  /** Zustimmung erteilt — der biometrische Ablauf startet. */
  onAccept: () => void;
  /** Abgelehnt — die Prüfung läuft ohne Biometrie weiter (`document_only`). */
  onDecline: () => void;
  /**
   * Kontaktadresse für Widerruf und Rückfragen — die Mail des Standorts, an dem
   * der Kunde registriert ist, übergeben per `contact_email` im Link.
   *
   * Bewusst optional: Fehlt sie, wird auf die Fahrschule verwiesen statt eine
   * Adresse zu erfinden. Eine falsche Widerrufsadresse ist schlimmer als keine —
   * der Widerruf muss ankommen, sonst ist die Einwilligung angreifbar.
   */
  contactEmail?: string;
  /**
   * Aufbewahrungsdauer der Aufnahmen in Tagen.
   *
   * MUSS zu DATA_RETENTION_DAYS auf dem Server passen. Weicht der angezeigte
   * Wert von der tatsächlichen Löschfrist ab, ist die Einwilligung fehlerhaft
   * informiert — beides nur gemeinsam ändern.
   */
  retentionDays: number;
}

const BiometricConsent: React.FC<BiometricConsentProps> = ({
  onAccept,
  onDecline,
  contactEmail,
  retentionDays,
}) => {
  const { t } = useI18n();

  const card: React.CSSProperties = {
    maxWidth: 560,
    margin: '0 auto',
    padding: '24px 20px 32px',
    lineHeight: 1.6,
  };
  const heading: React.CSSProperties = {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16,
    color: 'var(--ink)',
  };
  const sectionTitle: React.CSSProperties = {
    fontWeight: 600,
    marginTop: 20,
    marginBottom: 4,
    color: 'var(--ink)',
  };
  const body: React.CSSProperties = { color: 'var(--ink-muted, #4b5563)' };
  // Mindestens 48 px hoch — auf dem Handy mit dem Daumen bedienbar
  const primaryBtn: React.CSSProperties = {
    width: '100%',
    minHeight: 52,
    marginTop: 28,
    fontSize: 17,
    fontWeight: 600,
    borderRadius: 10,
    border: 'none',
    background: 'var(--accent, #2563eb)',
    color: '#fff',
    cursor: 'pointer',
  };
  const secondaryBtn: React.CSSProperties = {
    width: '100%',
    minHeight: 48,
    marginTop: 12,
    fontSize: 15,
    borderRadius: 10,
    background: 'transparent',
    border: '1px solid var(--border, #d1d5db)',
    color: 'var(--ink-muted, #4b5563)',
    cursor: 'pointer',
  };

  return (
    <div style={card}>
      <h2 style={heading}>{t('Consent for the face match')}</h2>

      <p style={body}>
        {t('For this check we compare your face with the photo in your document. We need your explicit consent for that.')}
      </p>

      <div style={sectionTitle}>{t('What happens')}</div>
      <p style={body}>
        {t('You read in your document and then record a short video in which you turn your head. A computer compares both faces and checks that a real person is in front of the camera.')}
      </p>

      <div style={sectionTitle}>{t('Why')}</div>
      <p style={body}>
        {t('So we can be sure that you are the person shown in the document.')}
      </p>

      <div style={sectionTitle}>{t('How long we keep it')}</div>
      <p style={body}>
        {t('The recordings are deleted after at most {days} days. We keep the result of the check for longer, without the images.')
          .replace('{days}', String(retentionDays))}
      </p>

      <div style={sectionTitle}>{t('Voluntary')}</div>
      <p style={body}>
        {t('You do not have to agree. Without the face match we only check your document — that continues right here and takes even less time. You will not suffer any disadvantage.')}
      </p>

      <div style={sectionTitle}>{t('Withdrawal')}</div>
      <p style={body}>
        {contactEmail ? (
          <>
            {t('You can withdraw your consent at any time — informally by email to')}{' '}
            <strong>{contactEmail}</strong>.{' '}
          </>
        ) : (
          <>{t('You can withdraw your consent at any time — informally at your driving school.')}{' '}</>
        )}
        {t('We will delete the recordings immediately. Checks already completed are not affected.')}
      </p>

      <button style={primaryBtn} onClick={onAccept}>
        {t('I agree')}
      </button>
      <button style={secondaryBtn} onClick={onDecline}>
        {t('Continue without face match')}
      </button>
    </div>
  );
};

export default BiometricConsent;
